import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Search, Upload, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const eur = (v) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtD = (s) => { if (!s) return ''; const d = new Date(s); return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear().toString().slice(-2)}`; };

function StatCard({ label, value, accent, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-xl font-bold ${accent || 'text-slate-800'}`}>{value}</p>
    </Card>
  );
}

function DetailPanel({ code }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/gestione-spese/property/${encodeURIComponent(code)}`)
      .then(res => setDetail(res.data))
      .catch(() => toast.error('Errore caricamento dettaglio'))
      .finally(() => setLoading(false));
  }, [code]);
  if (loading) return <div className="p-4 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Carico dettaglio…</div>;
  if (!detail) return null;
  return (
    <div className="p-4 bg-slate-50 space-y-4" data-testid={`gs-detail-${code}`}>
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Somme da Recuperare — trattenute sulle prenotazioni ({detail.deductions.length})</p>
        {detail.deductions.length === 0 ? (
          <p className="text-xs text-slate-400">Nessuna trattenuta ancora applicata alle prenotazioni di questa casa.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-500 border-b">
                <th className="py-1 pr-2">Prenotazione</th><th className="py-1 pr-2">Soggiorno</th>
                <th className="py-1 pr-2">Voce spesa</th><th className="py-1 pr-2 text-right">Scalato</th>
                <th className="py-1 pr-2 text-right">Resid. voce</th><th className="py-1"></th>
              </tr></thead>
              <tbody>
                {detail.deductions.map((d, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${d.cancelled ? 'opacity-40 line-through' : ''}`}>
                    <td className="py-1 pr-2 font-mono">{d.booking_code}</td>
                    <td className="py-1 pr-2">{fmtD(d.check_in)} → {fmtD(d.check_out)}</td>
                    <td className="py-1 pr-2">{d.description}</td>
                    <td className="py-1 pr-2 text-right font-semibold">{eur(d.amount)}</td>
                    <td className="py-1 pr-2 text-right text-slate-500">{d.expense_remaining != null ? eur(d.expense_remaining) : '—'}</td>
                    <td className="py-1">
                      {d.is_future && !d.cancelled && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">FUTURA</span>}
                      {d.cancelled && <span className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded text-[10px] font-semibold">ANNULLATA</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Spese Proprietario Pagamenti ({detail.owner_costs.length}) <span className="font-normal text-xs text-slate-400">— caricate dai proprietari, dividono cost/rsrv nei pagamenti; elenco separato dalle somme da recuperare</span></p>
        {detail.owner_costs.length === 0 ? (
          <p className="text-xs text-slate-400">Nessun costo sincronizzato dall'API per questa casa.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {detail.owner_costs.map((c, i) => (
              <span key={i} className="bg-white border border-slate-200 rounded px-2 py-0.5 text-xs text-slate-600">
                {c.description} · <b>{eur(c.amount)}</b>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GestioneSpesePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback((q = '') => {
    setLoading(true);
    api.get('/gestione-spese/summary', { params: { search: q } })
      .then(res => setData(res.data))
      .catch(() => toast.error('Errore caricamento Gestione Spese'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Sostituire la base dati con "${file.name}"? L'export attuale verrà rimpiazzato.`)) { e.target.value = ''; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/gestione-spese/import', fd);
      toast.success(`Import completato: ${res.data.imported} case (snapshot ${res.data.snapshot_date})`);
      load(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore import CSV');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const [syncing, setSyncing] = useState(false);
  const syncFromHw = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/gestione-spese/sync-from-hw');
      toast.success(`Ledger aggiornato dall'API HW: ${res.data.imported} case (snapshot ${res.data.snapshot_date})`);
      load(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore sync da API HW');
    } finally {
      setSyncing(false);
    }
  };

  const [sort, setSort] = useState({ key: 'remaining_now', dir: 'desc' });
  const toggleSort = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));
  const sortedRows = useMemo(() => {
    const rows = [...(data?.rows || [])];
    const { key, dir } = sort;
    rows.sort((a, b) => {
      if (key === 'alloggio') {
        const va = a.property_code || '', vb = b.property_code || '';
        return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      const va = Number(a[key] || 0), vb = Number(b[key] || 0);
      return dir === 'asc' ? va - vb : vb - va;
    });
    return rows;
  }, [data, sort]);
  const Th = ({ k, label, align = 'text-right' }) => (
    <th className={`py-2 pr-2 ${align} cursor-pointer select-none hover:text-slate-800`}
      onClick={() => toggleSort(k)} title="Clicca per ordinare" data-testid={`gs-sort-${k}`}>
      {label}{sort.key === k ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );

  const t = data?.totals;
  return (
    <div className="p-6 space-y-5" data-testid="gestione-spese-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gestione Spese</h1>
          <p className="text-sm text-slate-500">
            <b>Somme da Recuperare</b>: spese sostenute da noi per i proprietari — caricato, scalato dalle prenotazioni e restante da recuperare.
            Le <b>Spese Proprietario Pagamenti</b> (caricate dai proprietari, dividono cost/rsrv) sono elencate a parte nel dettaglio di ogni casa.
            {data?.meta?.snapshot_date && <> Base dati: export Net-Reverse del <b>{fmtD(data.meta.snapshot_date)}</b> ({data.meta.rows} case), aggiornata con le trattenute sincronizzate.</>}
          </p>
        </div>
        <div className="flex gap-2">
          <input type="file" accept=".csv" ref={fileRef} className="hidden" onChange={onUpload} data-testid="gs-upload-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="gs-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Carica nuovo export CSV
          </Button>
          <Button variant="outline" onClick={syncFromHw} disabled={syncing}
            title="Aggiorna la baseline direttamente dall'API Net-Reverse (quando l'endpoint /expense-ledger sarà attivo)"
            data-testid="gs-sync-hw-btn">
            {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Sincronizza da API HW
          </Button>
          <Button variant="outline" onClick={() => load(search)} data-testid="gs-reload-btn"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Case con spese" value={t.houses} testid="gs-stat-houses" />
          <StatCard label="Totale caricato" value={eur(t.total)} testid="gs-stat-total" />
          <StatCard label="Pagato / pianificato" value={eur(t.paid)} accent="text-emerald-600" testid="gs-stat-paid" />
          <StatCard label="Restante da recuperare" value={eur(t.remaining)} accent="text-red-600" testid="gs-stat-remaining" />
          <StatCard label="Su prenotazioni future" value={eur(t.future_planned)} accent="text-blue-600" testid="gs-stat-future" />
        </div>
      )}

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && load(search)}
              placeholder="Cerca per codice o nome casa…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="gs-search" />
          </div>
          <Button onClick={() => load(search)} data-testid="gs-search-btn">Cerca</Button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : !data?.rows?.length ? (
          <p className="py-8 text-center text-sm text-slate-400">
            {data?.meta?.snapshot_date ? 'Nessuna casa trovata.' : 'Nessuna base dati: carica l\u2019export CSV "case con spese restanti" di Net-Reverse.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b text-xs">
                  <Th k="alloggio" label="ALLOGGIO" align="text-left" />
                  <Th k="voci" label="VOCI" align="text-center" />
                  <Th k="total_amount" label="TOTALE" />
                  <Th k="paid_now" label="PAGATO/PIANIF." />
                  <Th k="recovered_after_snapshot" label="NUOVE TRATT." />
                  <Th k="remaining_now" label="RESTANTE" />
                  <Th k="future_planned" label="SU FUTURE" />
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, i) => (
                  <React.Fragment key={`${r.property_code}-${i}`}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`gs-row-${r.property_code}`}
                      onClick={() => setOpen(open === r.property_code ? null : r.property_code)}>
                      <td className="py-2 pr-2">
                        <span className="font-mono font-semibold">{r.property_code}</span>
                        <span className="text-slate-500 ml-2 text-xs">{(r.property_name || '').replace(r.property_code, '').replace(/^[\s-–]+/, '').slice(0, 55)}</span>
                        {!r.in_anagrafica && <span className="ml-2 bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">{r.nota ? r.nota.toUpperCase().slice(0, 40) : 'NON IN ANAGRAFICA'}</span>}
                      </td>
                      <td className="py-2 pr-2 text-center">{r.voci}</td>
                      <td className="py-2 pr-2 text-right">{eur(r.total_amount)}</td>
                      <td className="py-2 pr-2 text-right text-emerald-700">{eur(r.paid_now)}</td>
                      <td className="py-2 pr-2 text-right">{r.recovered_after_snapshot > 0 ? <span className="text-blue-700 font-semibold">{eur(r.recovered_after_snapshot)}</span> : <span className="text-slate-300">—</span>}</td>
                      <td className="py-2 pr-2 text-right font-bold text-red-600">{eur(r.remaining_now)}</td>
                      <td className="py-2 pr-2 text-right">{r.future_planned > 0 ? <span className="text-blue-600">{eur(r.future_planned)}</span> : <span className="text-slate-300">—</span>}</td>
                      <td className="py-2 text-slate-400">{open === r.property_code ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</td>
                    </tr>
                    {open === r.property_code && (
                      <tr><td colSpan={8}><DetailPanel code={r.property_code} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
